import { BED_JOINT_GAP_MM, BRICK_SPECS } from '@bw/core';
import type { CourseSpan, Mm, PlannedBrick } from '@bw/core';
import type { PlanSpan } from '@bw/document';
import { JOIN_TOLERANCE_MM } from './footprint.js';
import { planExtentOf } from './assemble.js';
import { wallToPlan } from './transform.js';
import type { BuildingModel, PlacedWall, Vec2 } from './types.js';

/**
 * Bonding the corners — walls that meet as a building's do.
 *
 * Two walls whose centrelines meet at a corner both want the same square of
 * space: each runs half its thickness past the meeting point. Left alone they
 * interpenetrate there, and the quarter on the OUTSIDE of the corner is filled
 * by neither, so the building has a notch at every corner.
 *
 * A bricklayer solves this by bonding the quoin: course by course, one wall
 * runs through to the far face of the other and the other stops short against
 * it, and on the next course they swap. Nothing overlaps, nothing is missing,
 * and the corner is tied together instead of being two walls leaning on one
 * another.
 *
 * That has to be decided BEFORE the bricks are laid, which is the whole reason
 * this produces a `CourseSpan` for the bond generator rather than a list of
 * edits to existing brickwork. Half a wall thickness is always five
 * millimetres off a whole number of catalogue units, so a course laid to the
 * old line and trimmed afterwards can never close the gap. Laid from the
 * return line it is exact — and the two return lines differ by a thickness
 * plus a joint, which for every real wall thickness (215, 327.5, 440) is a
 * whole number of units, so the bond stays in half bond across the swap.
 */

/** Which end of a wall, in its own x. */
export type WallEndSide = 'start' | 'end';

/** One end of one wall, where its centreline stops. */
export interface WallEnd {
  readonly wallId: string;
  readonly side: WallEndSide;
  readonly at: Vec2;
  readonly thicknessMm: Mm;
  /** Local x of this end — the design line, not the brickwork. */
  readonly localX: Mm;
  /** Unit vector along the wall, pointing away from this end. */
  readonly along: Vec2;
}

/** Two wall ends meeting at a point. */
export interface Junction {
  readonly at: Vec2;
  readonly ends: readonly [WallEnd, WallEnd];
  /** The turn between the two walls, 0 to 180 degrees. */
  readonly angleDeg: number;
  /**
   * True when the two are square enough to bond. An angle that is not a right
   * angle needs cut bricks this catalogue does not have, so it is reported and
   * left alone rather than bonded wrongly.
   */
  readonly square: boolean;
}

/** How far off a right angle a junction may be and still be bondable. */
const SQUARE_TOLERANCE_DEG = 1;

/** One course of ordinary bricks: 65 mm plus its bed joint. */
export const COURSE_PITCH_MM = BRICK_SPECS.stretcher.height + BED_JOINT_GAP_MM;

/**
 * Where wall ends meet.
 *
 * Ends within `toleranceMm` of each other are one junction, the same rule the
 * footprint uses to decide whether an outline closes. Only pairs are returned:
 * three walls meeting at a point is a valid thing to draw and not a quoin, so
 * it is left for `unbondable` to report rather than bonded as if it were one.
 */
export function findJunctions(
  walls: readonly PlacedWall[],
  toleranceMm: number = JOIN_TOLERANCE_MM,
  spans: ReadonlyMap<string, PlanSpan> = new Map(),
): Junction[] {
  const ends: WallEnd[] = [];
  for (const wall of walls) {
    const extent = spans.get(wall.id) ?? planExtentOf(wall.source);
    if (extent === null) continue;
    const half = wall.placement.thicknessMm / 2;
    // Straight off the placement, not off `centreline`: a wall that has been
    // drawn but not laid has no centreline yet, and that is exactly the wall
    // whose corners the editor needs to know about before it fills it.
    const turn = (wall.placement.rotationDeg * Math.PI) / 180;
    const along: Vec2 = { x: Math.cos(turn), z: -Math.sin(turn) };
    ends.push({
      wallId: wall.id,
      side: 'start',
      at: wallToPlan(wall.placement, extent.minX, half),
      thicknessMm: wall.placement.thicknessMm,
      localX: extent.minX,
      along,
    });
    ends.push({
      wallId: wall.id,
      side: 'end',
      at: wallToPlan(wall.placement, extent.maxX, half),
      thicknessMm: wall.placement.thicknessMm,
      localX: extent.maxX,
      along: { x: -along.x, z: -along.z },
    });
  }

  const groups: WallEnd[][] = [];
  for (const end of ends) {
    const found = groups.find((group) => {
      const first = group[0] as WallEnd;
      return Math.hypot(first.at.x - end.at.x, first.at.z - end.at.z) <= toleranceMm;
    });
    if (found === undefined) groups.push([end]);
    else found.push(end);
  }

  const out: Junction[] = [];
  for (const group of groups) {
    if (group.length !== 2) continue;
    const [a, b] = group as [WallEnd, WallEnd];
    if (a.wallId === b.wallId) continue;
    const angle = angleBetween(a, b);
    out.push({
      at: a.at,
      ends: [a, b],
      angleDeg: angle,
      square: Math.abs(angle - 90) <= SQUARE_TOLERANCE_DEG,
    });
  }
  return out;
}

/** The turn between two walls, taking each one's direction away from the join. */
function angleBetween(a: WallEnd, b: WallEnd): number {
  const dot = a.along.x * b.along.x + a.along.z * b.along.z;
  const cross = a.along.x * b.along.z - a.along.z * b.along.x;
  return Math.abs((Math.atan2(Math.abs(cross), dot) * 180) / Math.PI);
}

/**
 * Where one end of one wall stops, on each of the two kinds of course.
 *
 * `through` is the far face of the wall it meets, so a course laid to it fills
 * the whole corner. `short` is that wall's near face plus a joint, so a course
 * laid to it stops clear of everything the other wall occupies.
 */
export interface Return {
  readonly through: Mm;
  readonly short: Mm;
  /**
   * Whether this end runs through on an even course. The two walls at a
   * junction always take opposite values, which is what makes them alternate.
   */
  readonly throughOnEven: boolean;
}

export interface WallReturns {
  readonly wallId: string;
  readonly span: PlanSpan;
  readonly start: Return | null;
  readonly end: Return | null;
}

/**
 * The returns for every wall in a building.
 *
 * Only square junctions between exactly two walls get one; everything else is
 * left with a null end and reported by `unbondableJunctions`, because a wall
 * whose corner cannot be bonded should be laid to its plain design line rather
 * than to a guess.
 */
export function wallReturns(
  building: BuildingModel,
  perpendGapMm: number,
  toleranceMm: number = JOIN_TOLERANCE_MM,
  /**
   * Design lines to use instead of the ones the walls carry. The editor needs
   * this: a wall being filled for the first time has no length in plan yet, so
   * it cannot be seen to have a corner until the outline about to be laid is
   * taken as its length.
   */
  spans: ReadonlyMap<string, PlanSpan> = new Map(),
): Map<string, WallReturns> {
  const out = new Map<string, WallReturns>();
  for (const wall of building.walls) {
    const extent = spans.get(wall.id) ?? planExtentOf(wall.source);
    if (extent === null) continue;
    out.set(wall.id, {
      wallId: wall.id,
      span: { minX: extent.minX, maxX: extent.maxX },
      start: null,
      end: null,
    });
  }

  for (const junction of findJunctions(building.walls, toleranceMm, spans)) {
    if (!junction.square) continue;
    const [a, b] = junction.ends;
    // Symmetric and stable: the wall whose id sorts first takes the even
    // courses, so the two never both run through and never both stop short,
    // and a corner looks the same every time the project is opened.
    //
    // Which of them takes which course is all this has to decide, and it can
    // be decided one corner at a time. A course that runs through at one end
    // and stops short at the other gains half a thickness at one and loses it
    // at the other, so its length comes out the same as one that does neither
    // — there is nothing to be had from colouring the walls to make them agree
    // along their whole length. Whether a course then lands exactly on its
    // return is a question about the wall's dimensions, not about which end
    // does what.
    const aFirst = a.wallId < b.wallId;
    apply(out, a, b.thicknessMm, perpendGapMm, aFirst);
    apply(out, b, a.thicknessMm, perpendGapMm, !aFirst);
  }
  return out;
}

function apply(
  out: Map<string, WallReturns>,
  end: WallEnd,
  otherThicknessMm: Mm,
  perpendGapMm: number,
  throughOnEven: boolean,
): void {
  const current = out.get(end.wallId);
  if (current === undefined) return;
  const half = otherThicknessMm / 2;
  const outward = end.side === 'start' ? -1 : 1;
  const value: Return = {
    through: end.localX + outward * half,
    short: end.localX - outward * (half + perpendGapMm),
    throughOnEven,
  };
  out.set(end.wallId, { ...current, [end.side]: value });
}

/** Junctions this cannot bond, and why — for the caller to report. */
export function unbondableJunctions(
  building: BuildingModel,
  toleranceMm: number = JOIN_TOLERANCE_MM,
  spans: ReadonlyMap<string, PlanSpan> = new Map(),
): { readonly at: Vec2; readonly reason: string }[] {
  const out: { at: Vec2; reason: string }[] = [];
  for (const junction of findJunctions(building.walls, toleranceMm, spans)) {
    if (junction.square) continue;
    out.push({
      at: junction.at,
      reason: `two walls meet at ${junction.angleDeg.toFixed(1)}°, which needs cut bricks`,
    });
  }
  return out;
}

/**
 * Turn a wall's returns into the per-course spans the bond generator wants.
 *
 * Keyed on height rather than on a course number, because the number the
 * generator counts is local to whatever envelope it was handed. Two walls
 * meeting at a corner have their courses at the same world heights, and that
 * is the only thing they can both agree on.
 */
export function courseSpanFor(returns: WallReturns, originY = 0): CourseSpan {
  return (y: Mm) => {
    const index = Math.round((originY + y) / COURSE_PITCH_MM);
    const even = ((index % 2) + 2) % 2 === 0;
    const at = (value: Return | null, fallback: Mm): Mm =>
      value === null ? fallback : value.throughOnEven === even ? value.through : value.short;
    return {
      from: at(returns.start, returns.span.minX),
      to: at(returns.end, returns.span.maxX),
    };
  };
}

/**
 * How far the laid courses fell short of the corners they were set out for.
 *
 * `planBond`'s own `shortfallXMm` cannot answer this for a shape fill, because
 * that path lays with an overhang and trims afterwards — the shortfall is
 * always consumed by the unit hanging past the end. So it is measured from the
 * bricks that survived instead.
 *
 * Anything above zero means a course did not reach its return line, and the
 * gap it leaves runs the height of the wall. It happens when the wall's length
 * does not suit the bond: half a wall thickness is always five millimetres off
 * a whole unit, so a wall bonded at one end and plainly butted at the other can
 * never finish flush at both.
 */
export function reachShortfall(
  bricks: readonly PlannedBrick[],
  courseSpan: CourseSpan,
): Mm {
  const rows = new Map<number, { min: Mm; max: Mm }>();
  for (const brick of bricks) {
    const row = rows.get(brick.y);
    if (row === undefined) rows.set(brick.y, { min: brick.x, max: brick.x + brick.width });
    else {
      row.min = Math.min(row.min, brick.x);
      row.max = Math.max(row.max, brick.x + brick.width);
    }
  }

  let worst = 0;
  for (const [y, row] of rows) {
    const span = courseSpan(y);
    worst = Math.max(worst, row.min - span.from, span.to - row.max);
  }
  return worst;
}
